import { Box, Typography } from '@mui/material';
import { T, Icon } from '../ProjectOverview/parts/stitch';
import { isTaakAfgerond, taskTypeColors } from '../ProjectOverview/parts/projectMeta';
import { taakDatumInfo, taakEigenaar } from './rustigeKop';
import { useProjectDataOptioneel } from '../ProjectOverview/ProjectDataContext';
import { draaidagTaakWeergave } from '../../utils/draaidagTaak';

/**
 * Taakregel in de fasekaart. Zelfde gedrag als TaskItem (klik = openen,
 * rondje = afvinken), maar met één kleurbetekenis: rood = te laat. De
 * prioriteit staat er stil (grijze "P1"), een verlopen datum als rode tekst
 * "3 aug · 53 d te laat" (planning-gauntlet, 25 sep 2026).
 */
function FaseTaakRij({ task, onClick, onToggleComplete, vandaag, ingesprongen = false, voorNaam = null, rechts = null, draaidagen: draaidagenProp }) {
  const f = task.fields || {};
  // Draaidagtaak: crew van de gekoppelde draaidag i.p.v. de (mogelijk verouderde) taaknaam en
  // uitvoerder — externe crew staat alleen in de Outlook-titel (9604, 23 okt 2026).
  const ctx = useProjectDataOptioneel();
  const dd = draaidagTaakWeergave(task, draaidagenProp || ctx?.draaidagen);
  const klaar = isTaakAfgerond(task);
  const prio = String(f.PrioNr || '').replace(/^Prio (\d)$/, 'P$1');
  const datum = taakDatumInfo(task, vandaag);
  const eigenaar = dd ? '' : taakEigenaar(task);

  const wissel = (e) => {
    e.stopPropagation();
    onToggleComplete?.(task, !klaar);
  };

  return (
    // Subtaak (sep 2026): ingesprongen, zonder kaartrand en typestreep — hij hoort bij de
    // ouder erboven, niet naast hem.
    // Klik (of Enter op de gefocuste regel) = taak openen in het TaakPaneel; het rondje en
    // de acties rechts stoppen hun klik zelf (wiki 27 sep: regel moet het paneel openen).
    <Box onClick={onClick} role={ingesprongen ? 'listitem' : undefined}
      tabIndex={onClick ? 0 : undefined} data-taakregel={task.id}
      onKeyDown={onClick ? (e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); }
      } : undefined}
      sx={ingesprongen ? {
      display: 'flex', alignItems: 'center', gap: 1.25, pl: 1.25, pr: 1.5, py: 0.6, ml: 3.5,
      borderLeft: `1px solid ${T.surfaceHigh}`, cursor: onClick ? 'pointer' : 'default',
      '&:hover': { bgcolor: T.surfaceLow }, '&:hover .fase-rij-actie, &:focus-within .fase-rij-actie': { opacity: 1 },
    } : {
      display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.1,
      borderRadius: '0.75rem', bgcolor: klaar ? T.surfaceLow : T.surface,
      border: `1px solid ${T.surfaceHigh}`, borderLeft: `3px solid ${taskTypeColors[f.Type] || T.outlineVariant}`,
      cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s ease',
      '&:hover': { borderColor: T.outlineVariant },
      '&:hover .fase-rij-actie, &:focus-within .fase-rij-actie': { opacity: 1 },
    }}>
      {voorNaam}
      <Box component="button" type="button" onClick={wissel}
        aria-label={klaar ? 'Taak heropenen' : 'Taak afvinken'}
        sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <Icon name={klaar ? 'check_circle' : 'radio_button_unchecked'} size={ingesprongen ? 16 : 18}
          sx={{ color: klaar ? T.emerald : T.outlineVariant }} />
      </Box>
      <Typography sx={{
        flex: 1, minWidth: 0, fontFamily: T.fontBody, fontSize: ingesprongen ? '0.8rem' : '0.85rem', fontWeight: ingesprongen ? 400 : 500,
        color: klaar ? T.outlineVariant : T.text, textDecoration: klaar ? 'line-through' : 'none',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }} title={dd ? dd.titel : undefined} data-draaidag-crew={dd ? 'ja' : undefined}>
        {dd ? dd.naam : (f.NaamTaak || '-')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
        fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>
        {prio && <Box component="span" title={f.PrioNr}>{prio}</Box>}
        {datum.label && (
          <Box component="span" data-te-laat={datum.teLaat ? 'ja' : undefined}
            sx={datum.teLaat ? { color: T.error, fontWeight: 600 } : undefined}>
            {datum.label}
          </Box>
        )}
        {eigenaar && <Box component="span">{eigenaar}</Box>}
        {rechts}
      </Box>
    </Box>
  );
}

export default FaseTaakRij;
